/**
 * popupAuthOutcome — the REAL outcome of a popup the Auth SDK already called
 * "closed".
 *
 * `@firebase/auth` races two things in every popup operation: a close watcher
 * that, once the popup window is gone, waits a fixed 8 s and then settles the
 * operation with `auth/popup-closed-by-user`; and the actual sign-in requests
 * (`signInWithIdp` / the link variant, then the account reload), each bounded
 * only by the per-request API timeout (30 s in any browser — the 60 s variant is
 * Cordova / React Native only). On a weak network they outlive the 8 s, so
 * the caller hears "closed" while the request is still running. When it
 * answers, the SDK settles the operation a second time and its `debugAssert`
 * throws `INTERNAL ASSERTION FAILED: Pending promise was never set` as an
 * unhandled rejection:
 *
 *   - on success the user IS signed in / the provider IS linked — the SDK
 *     updates the user before it settles;
 *   - on failure the real error is replaced by the assertion and lost.
 *
 * So a caller that heard "closed" arms a window here, and the crash logger's
 * `unhandledrejection` handler hands the assertion to `claimLatePopupAssertion`
 * first. Inside the window the assertion is the late answer: the caller reads
 * the outcome from the state the SDK left behind (`succeeded`) and reports it,
 * and the rejection stays out of `crashLogs`. Outside a window the same message
 * is still logged — the redirect race (see redirectResultOnce.js) has no popup
 * in front of it, and a regression there must stay visible.
 *
 * No imports on purpose: the crash logger loads this, and the callers bring
 * Firebase and analytics themselves.
 *
 * See `docs/spec/19-infra.md`, "A popup that „closed" can still finish".
 */

/** The SDK's own wording — matched exactly, nothing looser. */
export const LATE_POPUP_ASSERTION = "INTERNAL ASSERTION FAILED: Pending promise was never set";

/**
 * How long a "closed" popup may still answer: the SDK's 8 s close grace plus
 * two 30 s requests (68 s), with margin.
 */
export const LATE_POPUP_WINDOW_MS = 90_000;

const POPUP_CLOSED = "auth/popup-closed-by-user";
const POPUP_CANCELLED = "auth/cancelled-popup-request";

/**
 * The one open window, or null. `olderUnanswered` counts earlier "closed"
 * popups whose late answer may still come: every answer carries the same
 * message, so one claimed here may be theirs.
 */
let pending = null;

/** True for the two codes a popup ends with when the SDK gave up on it. */
export function isPopupClosedCode(code) {
  return code === POPUP_CLOSED || code === POPUP_CANCELLED;
}

/**
 * Open the late-outcome window after a popup flow rejected.
 *
 * Only `auth/popup-closed-by-user` keeps the callbacks. A
 * `cancelled-popup-request` means a newer popup took over, and that one owns
 * what the user sees — the old answer may only be recognised, not reported.
 *
 * @param {string} code the popup's rejection code
 * @param {{ succeeded?: () => boolean, onSettled?: (ok: boolean|null) => void }} [handlers]
 */
export function armLatePopupOutcome(code, { succeeded, onSettled } = {}) {
  if (!isPopupClosedCode(code)) return;
  const reporting = code === POPUP_CLOSED;
  const now = Date.now();
  const open = pending && now <= pending.until;
  pending = {
    until: now + LATE_POPUP_WINDOW_MS,
    succeeded: reporting ? succeeded || null : null,
    onSettled: reporting ? onSettled || null : null,
    answered: false,
    olderUnanswered: open ? pending.olderUnanswered + (pending.answered ? 0 : 1) : 0,
  };
}

/**
 * A new interactive sign-in, link or re-auth is starting: an older flow's late
 * answer must not overwrite its outcome. The window stays open, so that late
 * assertion is still recognised.
 */
export function supersedeLatePopupOutcome() {
  if (!pending) return;
  pending.succeeded = null;
  pending.onSettled = null;
}

/**
 * The crash logger's first question for an unhandled rejection: is this the
 * late answer of a "closed" popup? Returns true when it is (and reports the
 * outcome once per window); the caller then logs nothing.
 */
export function claimLatePopupAssertion(reason) {
  const message = typeof reason === "string" ? reason : reason?.message;
  if (message !== LATE_POPUP_ASSERTION) return false;
  if (!pending) return false;
  if (Date.now() > pending.until) {
    pending = null;
    return false;
  }
  if (pending.answered) return true;
  const { succeeded, onSettled } = pending;
  const ok = succeeded ? !!succeeded() : null;
  // A success is true whichever popup produced it. Anything else, while an
  // older popup is still unanswered, is booked to that one and reported never:
  // this popup's own answer may still come and settle it.
  if (ok !== true && pending.olderUnanswered > 0) {
    pending.olderUnanswered -= 1;
    return true;
  }
  pending.answered = true;
  if (onSettled) {
    try {
      onSettled(ok);
    } catch {
      // A reporting bug must not turn the late answer back into a crash row.
    }
  }
  return true;
}

/** Test seam. */
export function __resetLatePopupOutcomeForTests() {
  pending = null;
}
